/**
 * useGlobalAPI - Unified hook for making API calls with automatic global filter application
 * 
 * This hook ensures ALL components automatically receive filtered OCEL data without
 * manual filter building. New components just use this hook and get global filtering for free.
 * 
 * Usage:
 *   const api = useGlobalAPI();
 *   const data = await api.post('/endpoint', { component_param: 'value' });
 *   // Global filters are automatically merged into the request
 */

import { useCallback } from 'react';
import { useGlobalFilters } from '../stores/GlobalFilterContext/GlobalFilterContext';
import { createFilteredRequest, makeRequest, makePostRequest } from '../services/apiClient';

export const useGlobalAPI = (activeFile = null) => {
  const { filterState } = useGlobalFilters();

  /**
   * Automatically merges global filters with any request data
   * @param {object} requestData - Component-specific request data
   * @param {string} fileType - File type override (defaults to activeFile or filterState.fileType)
   */
  const mergeGlobalFilters = useCallback((requestData = {}, fileType = null) => {
    const finalFileType = fileType || activeFile;
    if (!finalFileType) {
      // No silent fallback: requests without a dataset would resolve to the
      // server-global active file, which belongs to other users.
      throw new Error(
        'useGlobalAPI: no file_type available — pass activeFile to useGlobalAPI(activeFile) or a fileType override',
      );
    }

    const baseRequest = {
      file_type: finalFileType,
      ...requestData
    };

    // Always merge global filters
    const filteredRequest = createFilteredRequest(baseRequest, filterState);
    return filteredRequest;
  }, [filterState, activeFile]);

  /**
   * POST request with automatic global filter merging
   * @param {string} endpoint - API endpoint
   * @param {object} requestData - Component-specific data
   * @param {object} options - Additional options (timeout, headers, etc.)
   * @param {string} requestLabel - Label for logging
   * @param {string} fileType - File type override
   */
  const post = useCallback(async (endpoint, requestData = {}, options = {}, requestLabel = 'API request', fileType = null) => {
    const filteredRequest = mergeGlobalFilters(requestData, fileType);
    return await makePostRequest(endpoint, filteredRequest, options, requestLabel);
  }, [mergeGlobalFilters]);

  /**
   * RAW POST request (no global filters applied)
   * Mirrors legacy API used in previous branch
   */
  const postRaw = useCallback(async (endpoint, requestData = {}, options = {}, requestLabel = 'API request') => {
    return await makePostRequest(endpoint, requestData, options, requestLabel);
  }, []);

  /**
   * GET request with automatic global filter merging (for endpoints that support query params)
   * @param {string} endpoint - API endpoint
   * @param {object} requestData - Component-specific data (converted to query params)
   * @param {object} options - Additional options
   * @param {string} requestLabel - Label for logging
   * @param {string} fileType - File type override
   */
  const get = useCallback(async (endpoint, requestData = {}, options = {}, requestLabel = 'API request', fileType = null) => {
    const filteredRequest = mergeGlobalFilters(requestData, fileType);
    const configWithParams = {
      ...options,
      params: { ...options.params, ...filteredRequest }
    };
    return await makeRequest(endpoint, configWithParams, requestLabel);
  }, [mergeGlobalFilters]);

  /**
   * RAW GET request (no global filters applied)
   * requestData is converted to query params as-is
   */
  const getRaw = useCallback(async (endpoint, requestData = {}, options = {}, requestLabel = 'API request') => {
    const configWithParams = {
      ...options,
      params: { ...options.params, ...requestData }
    };
    return await makeRequest(endpoint, configWithParams, requestLabel);
  }, []);

  /**
   * Direct access to filtered request data (for custom API implementations)
   * @param {object} requestData - Component-specific data
   * @param {string} fileType - File type override
   */
  const buildFilteredRequest = useCallback((requestData = {}, fileType = null) => {
    return mergeGlobalFilters(requestData, fileType);
  }, [mergeGlobalFilters]);

  return {
    post,
    postRaw,
    get,
    getRaw,
    buildFilteredRequest,
    // Expose current filter state for debugging
    currentFilters: filterState,
    currentFile: activeFile
  };
};

/**
 * Higher-order component wrapper that provides useGlobalAPI to any component
 * Usage: export default withGlobalAPI(MyComponent);
 * 
 * NOTE: This requires JSX, so it should be used in .jsx files:
 * 
 * export const withGlobalAPI = (Component) => {
 *   return (props) => {
 *     const api = useGlobalAPI(props.activeFile);
 *     return React.createElement(Component, { ...props, api });
 *   };
 * };
 */